/**
 * Signature List Component
 *
 * Displays all signatures in a grid view with actions for each signature.
 * Allows setting default, editing metadata, and deleting signatures.
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Star, StarBorder, Edit, Delete } from '@mui/icons-material';
import { AppDispatch } from 'redux/store';
import {
    setDefaultSignature,
    deleteSignature,
    updateSignature,
} from 'redux/signatures/signatures.middleware';
import {
    selectSignatures,
    selectSignaturesUpdating,
    selectSignaturesDeleting,
} from 'redux/signatures/signatures.slice';
import type { AgentSignature } from 'types/signature';

export default function SignatureList() {
    const dispatch = useDispatch<AppDispatch>();
    const signatures = useSelector(selectSignatures);
    const isUpdating = useSelector(selectSignaturesUpdating);
    const isDeleting = useSelector(selectSignaturesDeleting);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [signatureToDelete, setSignatureToDelete] = useState<AgentSignature | null>(null);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [signatureToEdit, setSignatureToEdit] = useState<AgentSignature | null>(null);
    const [editFormData, setEditFormData] = useState({
        display_name: '',
        title: '',
        signature_name: '',
    });

    const handleSetDefault = async (signature: AgentSignature) => {
        try {
            await dispatch(setDefaultSignature(signature.id)).unwrap();
        } catch (error) {
            console.error('Failed to set default signature:', error);
        }
    };

    const handleOpenEdit = (signature: AgentSignature) => {
        setSignatureToEdit(signature);
        setEditFormData({
            display_name: signature.display_name || '',
            title: signature.title || '',
            signature_name: signature.signature_name || '',
        });
        setEditDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setSignatureToEdit(null);
        setEditFormData({ display_name: '', title: '', signature_name: '' });
    };

    const handleSaveEdit = async () => {
        if (!signatureToEdit) return;

        try {
            await dispatch(
                updateSignature({
                    id: signatureToEdit.id,
                    updates: {
                        display_name: editFormData.display_name.trim() || undefined,
                        title: editFormData.title.trim() || undefined,
                        signature_name: editFormData.signature_name.trim() || undefined,
                    },
                })
            ).unwrap();
            handleCloseEdit();
        } catch (error) {
            console.error('Failed to update signature:', error);
        }
    };

    const handleOpenDelete = (signature: AgentSignature) => {
        setSignatureToDelete(signature);
        setDeleteDialogOpen(true);
    };

    const handleCloseDelete = () => {
        setDeleteDialogOpen(false);
        setSignatureToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!signatureToDelete) return;

        try {
            await dispatch(deleteSignature(signatureToDelete.id)).unwrap();
            handleCloseDelete();
        } catch (error) {
            console.error('Failed to delete signature:', error);
        }
    };

    const renderSignaturePreview = (signature: AgentSignature) => {
        if (signature.creation_method === 'typed' && signature.typed_text) {
            const fontFamily = signature.font_family || 'Allura';
            const fontSize =
                signature.size === 'small' ? '24px' : signature.size === 'large' ? '48px' : '32px';

            return (
                <div
                    style={{
                        fontFamily: `'${fontFamily}', cursive`,
                        fontSize,
                        color: signature.color || '#000000',
                        textAlign: 'center',
                    }}
                >
                    {signature.typed_text}
                </div>
            );
        } else if (signature.creation_method === 'drawn' && signature.drawn_svg_data) {
            return (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        maxHeight: 100,
                        overflow: 'hidden',
                    }}
                    dangerouslySetInnerHTML={{ __html: signature.drawn_svg_data }}
                />
            );
        } else if (signature.creation_method === 'uploaded' && signature.uploaded_image_base64) {
            return (
                <img
                    src={`data:${signature.uploaded_image_mime_type};base64,${signature.uploaded_image_base64}`}
                    alt="Signature"
                    style={{
                        maxWidth: '100%',
                        maxHeight: 100,
                        objectFit: 'contain',
                    }}
                />
            );
        }

        return (
            <Typography variant="body2" color="text.secondary">
                No preview available
            </Typography>
        );
    };

    const getMethodLabel = (method: string) => {
        const labels: Record<string, string> = {
            typed: 'Typed',
            drawn: 'Drawn',
            uploaded: 'Uploaded',
        };
        return labels[method] || method;
    };

    if (signatures.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No signatures yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Create your first signature using the "Create Signature" tab
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Grid container spacing={3}>
                {signatures.map((signature) => (
                    <Grid item xs={12} sm={6} md={4} key={signature.id}>
                        <Card
                            elevation={signature.is_default ? 4 : 1}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                border: signature.is_default ? '2px solid #1976d2' : 'none',
                                position: 'relative',
                            }}
                        >
                            {signature.is_default && (
                                <Chip
                                    label="Default"
                                    color="primary"
                                    size="small"
                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                />
                            )}

                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box
                                    sx={{
                                        mb: 2,
                                        minHeight: 100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {renderSignaturePreview(signature)}
                                </Box>

                                <Typography variant="subtitle2" gutterBottom>
                                    {signature.signature_name ||
                                        `${getMethodLabel(signature.creation_method)} Signature`}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Method: {getMethodLabel(signature.creation_method)}
                                </Typography>

                                {signature.title && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                    >
                                        Title: {signature.title}
                                    </Typography>
                                )}

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Created: {new Date(signature.created_at).toLocaleDateString()}
                                </Typography>
                            </CardContent>

                            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleSetDefault(signature)}
                                    disabled={signature.is_default || isUpdating}
                                    title={
                                        signature.is_default ? 'Already default' : 'Set as default'
                                    }
                                >
                                    {signature.is_default ? (
                                        <Star color="primary" />
                                    ) : (
                                        <StarBorder />
                                    )}
                                </IconButton>

                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenEdit(signature)}
                                        disabled={isUpdating}
                                        title="Edit details"
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleOpenDelete(signature)}
                                        disabled={isDeleting}
                                        title="Delete signature"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Signature Details</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Display Name"
                            value={editFormData.display_name}
                            onChange={(e) =>
                                setEditFormData({ ...editFormData, display_name: e.target.value })
                            }
                            fullWidth
                            helperText="Override your name in agreements"
                        />

                        <TextField
                            label="Professional Title"
                            value={editFormData.title}
                            onChange={(e) =>
                                setEditFormData({ ...editFormData, title: e.target.value })
                            }
                            fullWidth
                            helperText="Credentials displayed below signature"
                        />

                        <TextField
                            label="Signature Name"
                            value={editFormData.signature_name}
                            onChange={(e) =>
                                setEditFormData({ ...editFormData, signature_name: e.target.value })
                            }
                            fullWidth
                            helperText="Label to identify this signature"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>Cancel</Button>
                    <Button
                        onClick={handleSaveEdit}
                        variant="contained"
                        disabled={isUpdating}
                        startIcon={isUpdating ? <CircularProgress size={16} /> : null}
                    >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} maxWidth="sm">
                <DialogTitle>Delete Signature?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this signature? This action cannot be
                        undone.
                    </Typography>
                    {signatureToDelete?.is_default && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This is your default signature. After deletion, you'll need to set
                            another signature as default.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} /> : null}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
